import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import csrfToken from '../../shared/adminCsrf';
import type { TreeLesson } from '../../shared/ProgramTree/useProgramTree';

// The unit's video: a ModelVideo onto the video library, as UnitSerializer#model_video sends it.
export interface UnitVideo {
  autoplay: boolean;
  id: number;
  poster_url: string | null;
  video_file_name: string | null;
  video_id: number;
}

// UnitSerializer with the edit form's extra attributes, for one program (publish and the call-out
// are per program).
export interface EditableUnit {
  automatic_translation_enabled: boolean;
  call_out_color: string | null;
  call_out_text: string | null;
  color: string | null;
  compelling_question: string | null;
  compelling_question_es: string | null;
  display_number: string;
  id: number;
  model_video: UnitVideo | null;
  phenomenon: string;
  poster_url: string | null;
  programs_count: number;
  publish: boolean;
  storyline: string | null;
  storyline_es: string | null;
  student_text_image_background_position: string;
  student_text_image_file_name: string | null;
  student_text_image_thumb_url: string | null;
  title: string;
  title_es: string | null;
  title_with_position: string;
  unit_projects_description_en: string | null;
}

const EXTRA_ATTRIBUTES = [
  'title_es', 'automatic_translation_enabled', 'compelling_question', 'compelling_question_es',
  'storyline', 'storyline_es', 'phenomenon', 'student_text_image_background_position',
  'student_text_image_file_name', 'unit_projects_description_en', 'model_video',
];

// Each section sends only its own fields; the API permits exactly this set plus the lesson moves.
export type UnitUpdatePayload = Partial<{
  automatic_translation_enabled: boolean;
  call_out_color: string;
  call_out_text: string;
  color: string;
  compelling_question: string;
  compelling_question_es: string;
  lesson_ids: number[];
  phenomenon: string;
  publish: boolean;
  remove_lesson_ids: number[];
  storyline: string;
  storyline_es: string;
  student_text_image_background_position: string;
  title: string;
  title_es: string;
  unit_projects_description_en: string;
}>;

export type UnitImageUploads = Partial<Record<'student_text_image', File>>;

export const unitQueryKey = (programId: number, unitId: number) => [QueryKeys.Unit, programId, unitId];

const unitPath = (programId: number, unitId: number, extra = false) => Routes.plato_api_unit_path(unitId, {
  program_id: programId,
  ...(extra ? { extra_attributes: EXTRA_ATTRIBUTES } : {}),
});

export const useUnit = (programId: number, unitId: number) => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ data: EditableUnit }>(unitPath(programId, unitId, true));
    return data.data;
  },
  queryKey: unitQueryKey(programId, unitId),
});

const toFormData = (unit: UnitUpdatePayload, uploads: UnitImageUploads) => {
  const form = new FormData();
  Object.entries(unit).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) value.forEach(item => form.append(`unit[${key}][]`, String(item)));
    else form.append(`unit[${key}]`, String(value));
  });
  Object.entries(uploads).forEach(([kind, file]) => {
    if (file) form.append(`unit[${kind}]`, file);
  });
  return form;
};

interface UpdateUnitInput {
  unit: UnitUpdatePayload;
  uploads?: UnitImageUploads;
}

// The saved unit comes back with the same extra attributes, so the page's copy is replaced from
// the response; the units list, the tree and the table of contents refetch their own shapes.
export const useUpdateUnit = (programId: number, unitId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ unit, uploads = {} }: UpdateUnitInput) => {
      const body = Object.values(uploads).some(Boolean) ? toFormData(unit, uploads) : { unit };
      return Axios.put<{ data: EditableUnit }>(unitPath(programId, unitId, true), body).then(res => res.data.data);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(unitQueryKey(programId, unitId), saved);
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ProgramUnits, programId] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Lessons, programId] });
    },
  });
};

// Reorders the unit's lessons: optimistically rewrites their slots in the tree's cached lessons,
// then posts the ids in their new order to the legacy reorder action — the same contract the table
// of contents uses per unit. The full lessons query refetches so the TOC agrees.
export const useReorderUnitLessons = (programId: number) => {
  const queryClient = useQueryClient();
  const treeKey = [QueryKeys.Lessons, programId, 'tree'];

  return (orderedLessons: TreeLesson[]) => {
    queryClient.setQueryData<TreeLesson[]>(treeKey, (all) => {
      if (!all) return all;

      const ids = new Set(orderedLessons.map(lesson => lesson.id));
      let next = 0;
      // eslint-disable-next-line no-plusplus
      return all.map(lesson => (ids.has(lesson.id) ? orderedLessons[next++] : lesson));
    });

    return Axios
      .post(
        Routes.reorder_admin_program_lessons_path(programId),
        { lesson: orderedLessons.map(lesson => lesson.id) },
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .then(() => queryClient.invalidateQueries({ exact: true, queryKey: [QueryKeys.Lessons, programId] }))
      .catch((error) => {
        queryClient.invalidateQueries({ queryKey: treeKey });
        throw error;
      });
  };
};

// The video library, searched by file name (the legacy VideoManager's picker).
export interface LibraryVideo {
  id: number;
  poster_url: string | null;
  video_file_name: string;
}

export const useVideoSearch = (query: string) => useQuery({
  enabled: query.trim().length > 0,
  queryFn: async () => {
    const { data } = await Axios.post<{ data: LibraryVideo[] }>(
      Routes.plato_api_search_videos_path({ search: { video_file_name_cont: query.trim() } }),
    );
    return data.data;
  },
  queryKey: [QueryKeys.Unit, 'video-search', query.trim()],
});

// Set (or replace) the unit's video: model_videos#create_unique keeps one ModelVideo per unit.
export const useSetUnitVideo = (programId: number, unitId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ autoplay, videoId }: { autoplay: boolean; videoId: number }) => Axios
      .post(Routes.plato_api_create_unique_model_videos_path(), {
        autoplay, model_id: unitId, model_type: 'Unit', video_id: videoId,
      })
      .then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unitQueryKey(programId, unitId) });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ProgramUnits, programId] });
    },
  });
};

export const useRemoveUnitVideo = (programId: number, unitId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelVideoId: number) => Axios.delete(Routes.plato_api_model_video_path(modelVideoId)),
    onSuccess: () => {
      queryClient.setQueryData<EditableUnit>(
        unitQueryKey(programId, unitId),
        unit => (unit ? { ...unit, model_video: null } : unit),
      );
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ProgramUnits, programId] });
    },
  });
};
